import type { BackendMessage, BackendMessageStatus } from '../types/backend';
import {
  POLL_DELAYED_THRESHOLD_MS,
  POLL_STUCK_THRESHOLD_MS,
  type ConversationLoadState,
  type SendBlockReason,
} from '../types/live';
import type { Message, MessageStatus } from '../types';

export interface SendBlockInput {
  conversationLoadState: ConversationLoadState;
  latestAssistantStatus: MessageStatus | null;
  conversationFull: boolean;
  /** Unix epoch ms; when set and in the future, sending is cooldown-blocked. */
  sendCooldownUntil: number | null;
  now?: number;
}

const TIMEZONE_SUFFIX = /(?:Z|[+-]\d{2}:\d{2})$/;

/** Map backend assistant message status to the frontend union. */
export function mapBackendMessageStatus(status: BackendMessageStatus): MessageStatus {
  switch (status) {
    case 'complete':
      return 'done';
    case 'failed':
      return 'error';
    case 'pending':
      return 'pending';
    case 'retracted':
      return 'retracted';
  }
}

/** Convert a backend wire message to the in-app `Message` shape. */
export function mapBackendMessage(message: BackendMessage): Message {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    created_at: message.created_at,
    status: mapBackendMessageStatus(message.status),
    guardrail_settled: message.guardrail_settled ?? true,
    replacement_message_id: message.replacement_message_id ?? null,
  };
}

/** True when the assistant row is visible but post-delivery guardrails may still change it. */
export function isUnsettledAssistantMessage(message: Message): boolean {
  return message.role === 'assistant' && message.status === 'done' && message.guardrail_settled === false;
}

/** True when polling should continue for guardrail settlement after a renderable reply. */
export function needsGuardrailSettlement(message: Message): boolean {
  return isUnsettledAssistantMessage(message);
}

/**
 * Parse an explicit UTC ISO 8601 timestamp from the backend.
 * Rejects timezone-less values so pending-age math stays correct.
 */
export function parseBackendUtcTimestamp(iso: string): number {
  const trimmed = iso.trim();
  if (!TIMEZONE_SUFFIX.test(trimmed)) {
    throw new Error('Ugyldigt tidsstempel fra serveren');
  }
  const ms = Date.parse(trimmed);
  if (Number.isNaN(ms)) {
    throw new Error('Ugyldigt tidsstempel fra serveren');
  }
  return ms;
}

export type PendingDisplayStatus = 'pending' | 'delayed' | 'stuck';

/**
 * Milliseconds the assistant row has been pending.
 * Uses the server `created_at` when known; otherwise elapsed time since polling began.
 */
export function getPendingAgeMs(
  createdAtMs: number | null,
  pollStartedAtMs: number,
  nowMs: number,
): number {
  if (createdAtMs !== null) {
    return Math.max(0, nowMs - createdAtMs);
  }
  return Math.max(0, nowMs - pollStartedAtMs);
}

/** Map pending age to the frontend pending, delayed, or stuck display status. */
export function derivePendingDisplayStatus(ageMs: number): PendingDisplayStatus {
  if (ageMs >= POLL_STUCK_THRESHOLD_MS) {
    return 'stuck';
  }
  if (ageMs >= POLL_DELAYED_THRESHOLD_MS) {
    return 'delayed';
  }
  return 'pending';
}

export function getLatestAssistantStatus(messages: Message[]): MessageStatus | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'assistant') {
      return messages[i].status;
    }
  }
  return null;
}

/**
 * Derive why sending is blocked for a conversation, or `null` when allowed.
 * Pure — no I/O or timers; `now` is injectable for tests.
 */
export function deriveSendBlockReason(input: SendBlockInput): SendBlockReason | null {
  const now = input.now ?? Date.now();

  if (input.conversationLoadState !== 'ready') {
    return 'unavailable';
  }

  if (input.sendCooldownUntil !== null && now < input.sendCooldownUntil) {
    return 'cooldown';
  }

  if (input.conversationFull) {
    return 'full';
  }

  if (input.latestAssistantStatus === 'pending') {
    return 'pending';
  }

  if (input.latestAssistantStatus === 'delayed') {
    return 'delayed';
  }

  if (input.latestAssistantStatus === 'streaming') {
    return 'pending';
  }

  return null;
}
